import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { GraphView } from '../components/shared/GraphView';
import { ResultCard } from '../components/shared/ResultCard';
import {
  Server, Users, ArrowLeft, AlertTriangle,
  Package, Shield, Check, X, Zap, Play, FileText, ChevronDown,
  RefreshCw, Target, Activity, Eye, Workflow, Bell, UserPlus
} from 'lucide-react';
import { services } from '../data/mockData';
import {
  driftFindings,
  serviceChangeSpecs,
  dependencyNodeDetails,
  failureSimulationResults,
  serviceCompiledDiffs,
  ownershipIssues,
  escalationRoutes
} from '../data/serviceAutomationData';
import { Button } from '../components/ui/button';

const dependencyNodes = [
  { id: 'payments-db', label: 'payments-db', type: 'database', x: 400, y: 50 },
  { id: 'checkout-service', label: 'checkout-service', type: 'service', x: 200, y: 150 },
  { id: 'order-service', label: 'order-service', type: 'service', x: 400, y: 150 },
  { id: 'refund-service', label: 'refund-service', type: 'service', x: 600, y: 150 },
  { id: 'auth-service', label: 'auth-service', type: 'service', x: 100, y: 50 },
  { id: 'frontend-web', label: 'frontend-web', type: 'app', x: 100, y: 280 },
  { id: 'mobile-api', label: 'mobile-api', type: 'app', x: 300, y: 280 },
  { id: 'support-portal', label: 'support-portal', type: 'app', x: 500, y: 280 },
  { id: 'finance-api', label: 'finance-api', type: 'app', x: 700, y: 280 }
];

const dependencyEdges = [
  { from: 'payments-db', to: 'checkout-service' },
  { from: 'payments-db', to: 'order-service' },
  { from: 'payments-db', to: 'refund-service' },
  { from: 'auth-service', to: 'checkout-service' },
  { from: 'auth-service', to: 'order-service' },
  { from: 'checkout-service', to: 'frontend-web' },
  { from: 'checkout-service', to: 'mobile-api' },
  { from: 'order-service', to: 'mobile-api' },
  { from: 'refund-service', to: 'support-portal' },
  { from: 'refund-service', to: 'finance-api' }
];

const severityColors = {
  high: { bg: 'bg-slate-900', text: 'text-white', border: 'border-slate-900' },
  medium: { bg: 'bg-slate-200', text: 'text-slate-700', border: 'border-slate-300' },
  low: { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/20' }
};

export const Services = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();

  // UI State
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodePanel, setShowNodePanel] = useState(false);
  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [simulationResults, setSimulationResults] = useState(null);
  const [showChangeSpecModal, setShowChangeSpecModal] = useState(null);
  const [expandedDrift, setExpandedDrift] = useState(null);

  const handleProposeBundle = (specId) => {
    const spec = serviceChangeSpecs[specId];
    if (spec) {
      navigate('/changes', {
        state: {
          proposeBundle: true,
          changeSpec: spec,
          compiledDiffs: serviceCompiledDiffs
        }
      });
    } else {
      navigate('/changes');
    }
  };

  const handleNodeClick = (nodeId) => {
    const nodeDetail = dependencyNodeDetails[nodeId];
    if (nodeDetail) {
      setSelectedNode({ id: nodeId, ...nodeDetail });
      setShowNodePanel(true);
    }
  };

  const handleSimulateFailure = (nodeId) => {
    const results = failureSimulationResults[nodeId];
    if (results) {
      setSimulationResults(results);
      setShowSimulationModal(true);
    }
  };

  const handleViewChangeSpec = (specId) => {
    const spec = serviceChangeSpecs[specId];
    if (spec) {
      setShowChangeSpecModal(spec);
    }
  };

  // Service Detail View
  if (serviceId) {
    const service = services.find(s => s.id === serviceId);
    if (!service) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-slate-500 mb-4">Service not found</p>
            <Link to="/services" className="text-primary hover:underline">Back to services</Link>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <Link to="/services" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Configuration Intelligence
        </Link>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-slate-900">SERVICE: {service.name}</h1>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  service.criticality === 'P1' ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-700'
                }`}>
                  {service.criticality} ({service.criticalityLabel})
                </span>
                <Button size="sm" onClick={() => handleProposeBundle('service-spec-checkout')}>
                  <Package className="w-3.5 h-3.5 mr-1" /> Propose Service Bundle
                </Button>
              </div>
            </div>
          </div>
            
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-100">
            <div className="bg-white p-5">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Type</p>
              <p className="text-sm text-slate-700">{service.type} ({service.technology})</p>
            </div>
            <div className="bg-white p-5">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Environment</p>
              <p className="text-sm text-slate-700">{service.environment}</p>
            </div>
            <div className="bg-white p-5">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Team</p>
              <p className="text-sm text-slate-700">{service.team}</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <ResultCard
            title="Ownership"
            sections={[{
              content: (
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Team</span>
                    <span className="font-medium">{service.team}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Tech Lead</span>
                    <span className="font-medium">{service.techLead}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">On-Call</span>
                    <span className="font-medium text-primary">{service.onCall} (PagerDuty)</span>
                  </div>
                </div>
              )
            }]}
            actions={[
              { label: 'Page team', primary: true },
              { label: 'Create escalation bundle', onClick: () => handleProposeBundle('escalation-route') }
            ]}
          />

          <ResultCard
            title="Infrastructure"
            sections={[{
              content: (
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Runs on</span>
                    <span className="font-medium">{service.infrastructure.runsOn}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Replicas</span>
                    <span className="font-medium">{service.infrastructure.replicas} pods</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">CPU</span>
                    <span className="font-medium">{service.infrastructure.cpu}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Memory</span>
                    <span className="font-medium">{service.infrastructure.memory}</span>
                  </div>
                </div>
              )
            }]}
            actions={[
              { label: 'Enforce baseline bundle', primary: true, onClick: () => handleProposeBundle('enforce-baseline-auth') }
            ]}
          />

          <ResultCard
            title="Dependencies (Upstream)"
            sections={[{
              content: (
                <div className="flex flex-wrap gap-2">
                  {service.dependencies.map((dep, idx) => (
                    <span key={idx} className="px-3 py-1.5 bg-slate-100 rounded-lg text-sm text-slate-700">
                      {dep}
                    </span>
                  ))}
                </div>
              )
            }]}
          />

          <ResultCard
            title="Dependents (Downstream)"
            sections={[{
              content: (
                <div className="flex flex-wrap gap-2">
                  {service.dependents.map((dep, idx) => (
                    <span key={idx} className="px-3 py-1.5 bg-primary/10 rounded-lg text-sm text-primary">
                      {dep}
                    </span>
                  ))}
                </div>
              )
            }]}
          />
        </div>
      </div>
    );
  }

  // Main Services/Config Intelligence View
  return (
    <div className="space-y-6" data-testid="services-page">
      {showChangeSpecModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowChangeSpecModal(null)}>
          <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-500" />
                <h3 className="font-semibold text-slate-900">Change Spec: {showChangeSpecModal.id}</h3>
              </div>
              <button onClick={() => setShowChangeSpecModal(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)]">
              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-slate-100 font-mono whitespace-pre-wrap">
                  {JSON.stringify(showChangeSpecModal, null, 2)}
                </pre>
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t flex gap-2">
              <Button variant="outline" onClick={() => setShowChangeSpecModal(null)}>
                Close
              </Button>
              <Button
                onClick={() => { setShowChangeSpecModal(null); navigate('/changes', { state: { proposeBundle: true, changeSpec: showChangeSpecModal } }); }}
              >
                <Package className="w-4 h-4 mr-2" />
                Propose Action Bundle
              </Button>
            </div>
          </div>
        </div>
      )}

      {showNodePanel && selectedNode && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setShowNodePanel(false)}>
          <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-lg border border-slate-200" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h3 className="font-semibold text-lg text-slate-900">{selectedNode.id}</h3>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  selectedNode.criticality === 'P1' ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-700'
                }`}>{selectedNode.criticality}</span>
              </div>
              <button onClick={() => setShowNodePanel(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2 text-slate-900">
                  <Target className="w-4 h-4 text-slate-900" /> Blast Radius
                </h4>
                <div className="bg-slate-100 border border-slate-200 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-600 text-xs uppercase">Level</p>
                      <p className="font-bold text-slate-900 text-lg">{selectedNode.blastRadius.level.toUpperCase()}</p>
                    </div>
                    <div>
                      <p className="text-slate-600 text-xs uppercase">Affected Users</p>
                      <p className="font-bold text-slate-900 text-lg">{selectedNode.blastRadius.affectedUsers}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Direct dependents</p>
                      <p className="font-medium">{selectedNode.blastRadius.directDependents}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Transitive dependents</p>
                      <p className="font-medium">{selectedNode.blastRadius.transitiveDependents}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2 text-slate-900">
                  <AlertTriangle className="w-4 h-4 text-slate-600" /> Single Points of Failure
                </h4>
                <div className="space-y-2">
                  {selectedNode.singlePointsOfFailure.map((spof, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-slate-100 border border-slate-200 rounded-lg text-sm">
                      <AlertTriangle className="w-4 h-4 text-slate-600 mt-0.5 shrink-0" />
                      <span className="text-slate-700">{spof}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2 text-slate-900">
                  <Activity className="w-4 h-4 text-primary" /> Recent Changes
                </h4>
                <div className="space-y-2">
                  {selectedNode.recentChanges.map((change, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg text-sm">
                      <span className="text-slate-400 text-xs w-20 shrink-0">{change.date}</span>
                      <div>
                        <p className="text-slate-700">{change.action}</p>
                        <p className="text-xs text-slate-500">by {change.by}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2 text-slate-900">
                  <Zap className="w-4 h-4 text-slate-500" /> Suggested Actions
                </h4>
                <div className="space-y-2">
                  {selectedNode.suggestedActions.map((action) => (
                    <div key={action.id} className="p-3 bg-white border border-slate-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm text-slate-900">{action.label}</span>
                        <Button size="sm" onClick={() => handleProposeBundle(action.id)}>
                          <Package className="w-3 h-3 mr-1" /> Bundle
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">{action.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => handleSimulateFailure(selectedNode.id)}
                className="w-full px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-2 text-slate-600 hover:text-primary"
              >
                <Play className="w-4 h-4" />
                Simulate Failure
              </button>
            </div>
          </div>
        </div>
      )}

      {showSimulationModal && simulationResults && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowSimulationModal(false)}>
          <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-100">
              <div className="flex items-center gap-2">
                <Play className="w-5 h-5 text-slate-700" />
                <h3 className="font-semibold text-slate-900">Failure Simulation Results</h3>
              </div>
              <button onClick={() => setShowSimulationModal(false)} className="p-1 hover:bg-slate-200 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              <div className="mb-6">
                <h4 className="font-medium mb-2 text-slate-900">{simulationResults.simulation}</h4>
                <p className="text-sm text-slate-500">Duration: {simulationResults.duration}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-600 uppercase">Impacted Users</p>
                  <p className="text-xl font-bold text-slate-900">{simulationResults.results.impactedUsers}</p>
                </div>
                <div className="p-4 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-600 uppercase">Revenue Impact</p>
                  <p className="text-xl font-bold text-slate-900">{simulationResults.results.revenueImpact}</p>
                </div>
                <div className="p-4 bg-slate-200 rounded-lg">
                  <p className="text-xs text-slate-600 uppercase">Current MTTR</p>
                  <p className="text-xl font-bold text-slate-700">{simulationResults.results.mttr}</p>
                </div>
                <div className="p-4 bg-primary/10 rounded-lg">
                  <p className="text-xs text-primary uppercase">MTTR with Hardening</p>
                  <p className="text-xl font-bold text-primary">{simulationResults.results.mttrWithHardening}</p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-medium mb-3 text-slate-900">Findings</h4>
                <div className="space-y-2">
                  {simulationResults.findings.map((finding, idx) => (
                    <div key={idx} className={`flex items-center gap-2 p-3 rounded-lg ${
                      finding.severity === 'critical' ? 'bg-slate-900 text-white' :
                      finding.severity === 'high' ? 'bg-slate-200 text-slate-700' :
                      'bg-primary/10 text-primary'
                    }`}>
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      <span className="text-sm">{finding.finding}</span>
                      <span className="ml-auto text-xs uppercase font-medium">{finding.severity}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3 text-slate-900">Hardening Recommendations</h4>
                <div className="space-y-2">
                  {simulationResults.hardeningRecommendations.map((rec, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      <span>{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t flex gap-2">
              <Button variant="outline" onClick={() => setShowSimulationModal(false)}>
                Close
              </Button>
              <Button onClick={() => { setShowSimulationModal(false); handleProposeBundle(simulationResults.changeSpecId); }}>
                <Package className="w-4 h-4 mr-2" />
                Create Hardening Bundle
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Configuration Intelligence</h1>
          <p className="text-slate-500">Real-time understanding of how everything is configured and connected</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Services', value: '127', icon: Server },
            { label: 'Drift Findings', value: driftFindings.length, icon: AlertTriangle, color: 'warning' },
            { label: 'Teams', value: '14', icon: Users },
            { label: 'No Owner', value: ownershipIssues.length, icon: UserPlus, color: 'critical' }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  stat.color === 'warning' ? 'bg-slate-200' :
                  stat.color === 'critical' ? 'bg-slate-900' :
                  'bg-primary/10'
                }`}>
                  <stat.icon className={`w-4.5 h-4.5 ${
                    stat.color === 'warning' ? 'text-slate-600' :
                    stat.color === 'critical' ? 'text-white' :
                    'text-primary'
                  }`} />
                </div>
                <span className="text-2xl font-bold text-slate-900">{stat.value}</span>
              </div>
              <p className="text-sm text-slate-600">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Drift and Policy Findings */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-slate-600" />
              <h2 className="text-lg font-semibold text-slate-900">Drift and Policy Findings</h2>
              <span className="px-2 py-0.5 bg-slate-200 text-slate-700 text-xs font-medium rounded-full">
                {driftFindings.length} findings
              </span>
            </div>
            <span className="text-sm text-slate-500">Intent in, action bundle out, agents execute</span>
          </div>

          <div className="space-y-3">
            {driftFindings.map((finding) => (
              <div
                key={finding.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                data-testid={`finding-${finding.id}`}
              >
                <div
                  className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpandedDrift(expandedDrift === finding.id ? null : finding.id)}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${severityColors[finding.severity].bg}`}>
                    <AlertTriangle className={`w-5 h-5 ${severityColors[finding.severity].text}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{finding.service}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityColors[finding.severity].bg} ${severityColors[finding.severity].text}`}>
                        {finding.severity}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">
                        {finding.type === 'policy_violation' ? 'policy violation' : 'drift'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{finding.finding}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleProposeBundle(`drift-revert-${finding.id.replace('drift-', '')}`); }}
                      className="px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg hover:bg-primary/90 flex items-center gap-1"
                    >
                      <Package className="w-3 h-3" /> Revert Bundle
                    </button>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedDrift === finding.id ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                {expandedDrift === finding.id && (
                  <div className="px-5 pb-4 pt-0 border-t border-slate-100 bg-slate-50/50">
                    <div className="pt-4 grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-slate-700 mb-2">Details</h4>
                        <div className="bg-white rounded-lg p-3 text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Expected:</span>
                            <span className="font-mono text-xs">{String(finding.details.expected)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Actual:</span>
                            <span className="font-mono text-xs text-slate-900">{String(finding.details.actual)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Changed by:</span>
                            <span className="text-xs">{finding.details.changedBy}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-slate-700 mb-2">Actions</h4>
                        <div className="space-y-2">
                          <button
                            onClick={() => handleProposeBundle(`drift-revert-${finding.id.replace('drift-', '')}`)}
                            className="w-full px-3 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 flex items-center justify-center gap-2"
                          >
                            <RefreshCw className="w-4 h-4" />
                            {finding.actions.revert.label}
                          </button>
                          <button
                            onClick={() => addToast('Creating baseline update bundle', 'info')}
                            className="w-full px-3 py-2 border border-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center justify-center gap-2"
                            disabled={finding.actions.accept.blocked}
                          >
                            <Check className="w-4 h-4" />
                            {finding.actions.accept.label}
                          </button>
                          <button
                            onClick={() => addToast('Creating exception with expiry', 'info')}
                            className="w-full px-3 py-2 border border-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center justify-center gap-2"
                          >
                            <Shield className="w-4 h-4" />
                            {finding.actions.exception.label}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Service Catalog */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Service Catalog</h2>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Service</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Criticality</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Team</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Dependencies</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {services.map((service) => (
                    <tr key={service.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4">
                        <Link to={`/services/${service.id}`} className="font-medium text-primary hover:underline">
                          {service.name}
                        </Link>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">{service.type}</td>
                      <td className="px-5 py-4">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          service.criticality === 'P1' ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-700'
                        }`}>
                          {service.criticality}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">{service.team}</td>
                      <td className="px-5 py-4 text-sm text-slate-600">{service.dependencies.length}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleProposeBundle('service-spec-checkout')}
                            className="px-2 py-1 text-xs text-primary hover:bg-primary/10 rounded flex items-center gap-1"
                            title="Create service spec bundle"
                          >
                            <FileText className="w-3 h-3" /> Spec
                          </button>
                          <button
                            onClick={() => handleProposeBundle('runbook-checkout')}
                            className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded flex items-center gap-1"
                            title="Generate runbook bundle"
                          >
                            <Workflow className="w-3 h-3" /> Runbook
                          </button>
                          <button
                            onClick={() => handleProposeBundle('enforce-baseline-auth')}
                            className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded flex items-center gap-1"
                            title="Enforce baseline bundle"
                          >
                            <RefreshCw className="w-3 h-3" /> Baseline
                          </button>
                          <button
                            onClick={() => handleNodeClick('checkout-service')}
                            className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded flex items-center gap-1"
                            title="View dependencies"
                          >
                            <Eye className="w-3 h-3" /> Deps
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Dependency Tree */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Dependency Tree</h2>
              <p className="text-sm text-slate-500">Click any node for details and automation actions</p>
            </div>
            <button
              onClick={() => handleSimulateFailure('payments-db')}
              className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-2 text-sm"
            >
              <Play className="w-4 h-4" />
              Simulate Failure
            </button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="relative">
              <GraphView
                nodes={dependencyNodes}
                edges={dependencyEdges}
                width={800}
                height={400}
                onNodeClick={handleNodeClick}
              />
              <div className="absolute top-2 right-2 text-xs text-slate-400">
                Click nodes for automation options
              </div>
            </div>
          </div>

          <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Direct dependents</p>
                <p className="text-xl font-bold text-slate-900">3</p>
              </div>
              <div>
                <p className="text-slate-500">Transitive dependents</p>
                <p className="text-xl font-bold text-slate-900">9</p>
              </div>
              <div>
                <p className="text-slate-500">Blast radius</p>
                <p className="text-xl font-bold text-slate-900">HIGH</p>
              </div>
            </div>
          </div>
        </div>

        {/* Ownership Issues */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-slate-900" />
              <h2 className="text-lg font-semibold text-slate-900">Ownership and Routing</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Missing Ownership */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-medium text-slate-900 mb-3">Services Without Owners</h3>
              <div className="space-y-3 mb-4">
                {ownershipIssues.map((issue) => (
                  <div key={issue.id} className="flex items-center justify-between p-3 bg-slate-100 border border-slate-200 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">{issue.service}</p>
                      <p className="text-xs text-slate-600">Last deployed: {issue.lastDeployed}</p>
                    </div>
                    <span className="text-xs text-slate-600">Suggested: {issue.suggestedOwner.team}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleProposeBundle('fix-ownership')}
                className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center justify-center gap-2"
              >
                <Package className="w-4 h-4" />
                Fix Missing Ownership Bundle
              </button>
            </div>

            {/* Escalation Routes */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-medium text-slate-900 mb-3">Escalation Routes</h3>
              <div className="space-y-3 mb-4">
                {escalationRoutes.map((route, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${
                    route.hasRoute ? 'bg-primary/10 border border-primary/20' : 'bg-slate-200 border border-slate-300'
                  }`}>
                    <div>
                      <p className={`font-medium ${route.hasRoute ? 'text-primary' : 'text-slate-700'}`}>{route.team}</p>
                      <p className={`text-xs ${route.hasRoute ? 'text-primary' : 'text-slate-600'}`}>
                        {route.services.length} services
                      </p>
                    </div>
                    {route.hasRoute ? (
                      <span className="text-xs text-primary">{route.levels}-tier escalation</span>
                    ) : (
                      <span className="text-xs text-slate-600">No route configured</span>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleProposeBundle('escalation-route')}
                className="w-full px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 flex items-center justify-center gap-2"
              >
                <Bell className="w-4 h-4" />
                Create Escalation Route Bundle
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
